import type { PropertyFilterQuery } from '@cloudscape-design/collection-hooks';
import _ from 'lodash';
import { useMemo } from 'react';
import { useDashboardWidgetSettings } from 'src/context/useDashboardWidgetSettings';

import type { StatefulTableOptions } from '@/utils/table/hooks/useGetStatelessTableProps';

import type { GigawidgetSettings } from '../../universal-widget/util';
import propertyFilter from '../property-filter';
import type {
  DataSourceData,
  DataSourceItem,
  DataSourceVariables,
  DateFilterOptions,
  WidgetDataSource,
  WidgetTableProps,
} from '../types';
import {
  useDataSourceFilter,
  useSeriesDateFilter,
} from './useDataSourceFilter';

type GetWidgetDataResult<TDataSource extends WidgetDataSource> = {
  loading?: boolean;
  /**
   * True when the widget query failed (either path). Distinct from an empty
   * result — widgets render a no-data state for errors, never a count of 0.
   */
  error?: boolean;
  tableProps: WidgetTableProps<DataSourceItem<TDataSource>>;
  data: DataSourceData<TDataSource> | undefined;
};

type UseGetWidgetDataOptions<TDataSource extends WidgetDataSource> = {
  dataSource: TDataSource;
  disableDashboardFilters?: boolean;
  dateFilterOptions?: DateFilterOptions<TDataSource>;
  /**
   * Bounds a time-series chart to the dashboard date range. Same callback shape
   * as `dateFilterOptions`, but deliberately a separate option because it must
   * NOT be subject to `ignoreDashboardDateFilter` — see `useSeriesDateFilter`.
   *
   * Covers both shapes an over-time chart needs: clipping a NESTED result
   * series (`resultHistoryWindow` on the four assessment-result charts) and
   * narrowing the rows when the rows ARE the series (`dateRange.field` on the
   * control-test chart).
   */
  seriesDateFilterOptions?: DateFilterOptions<TDataSource>;
  propertyFilterQuery?: PropertyFilterQuery;
  tableOptions?: Omit<
    StatefulTableOptions<DataSourceVariables<TDataSource>>,
    'propertyFilter'
  >;
};

export const useGetWidgetData = <TDataSource extends WidgetDataSource>(
  options: UseGetWidgetDataOptions<TDataSource>
): GetWidgetDataResult<TDataSource> => {
  const { dataSource, dateFilterOptions } = options;
  const [settings] = useDashboardWidgetSettings<GigawidgetSettings>();

  const queryArgs = useDataSourceFilter(dataSource, dateFilterOptions);
  // Merged separately below rather than through `useDataSourceFilter`, whose
  // `ignoreDashboardDateFilter` gate would discard it. A plain spread suffices:
  // these keys are top-level and never collide with the department / tag
  // filters, so there is nothing for a deep merge to reconcile.
  const seriesDateArgs = useSeriesDateFilter<TDataSource>(
    options.seriesDateFilterOptions
  );

  // No `fetchPolicy` and no `shouldSkip` — the loading term below assumes the
  // query is never skipped. COI-600 §4.1 called for a per-call `fetchPolicy`
  // (`cacheOnly ? 'cache-first' : 'no-cache'`); dropped as a deliberate
  // deviation because `cacheOnly` had no caller and every widget hook already
  // defaults to `no-cache`, so the behaviour is unchanged.
  // `suppressErrorNotification` is per-call (COI-691): a dashboard renders
  // many widgets at once and `useErrorNotification` toasts the raw message
  // with `duration: Infinity`.
  const { data, loading, error } = dataSource.useWidgetQuery({
    queryArgs: options.disableDashboardFilters
      ? {}
      : ({
          ...queryArgs,
          ...seriesDateArgs,
        } as DataSourceVariables<TDataSource>),
    suppressErrorNotification: true,
  });

  const filteredData = dataSource.useInitialDataFilter
    ? dataSource.useInitialDataFilter(
        data,
        Boolean(settings?.allowOwnershipFiltering)
      )
    : data;

  const tableProps = dataSource.useTablePropsHook(filteredData, {
    propertyFilter: options?.propertyFilterQuery,
    sortingState: options.tableOptions?.sortingState,
    setSortingState: options.tableOptions?.setSortingState ?? _.noop,
    preferences: options.tableOptions?.preferences,
    setPreferences: options.tableOptions?.setPreferences ?? _.noop,
    setPropertyFilter: _.noop,
    hideNoMatchClearButton: true,
  });

  // Keyed on the three real inputs, so a re-render that changes none of them
  // does not re-run an O(rows) filter (ENG-560). Note this only pays off while
  // `options.propertyFilterQuery` is itself stable — it is the widget's
  // sanitised `settings.filtering`, memoised in `useDashboardWidgetSettings`.
  const filteredItems = useMemo(
    () =>
      options.propertyFilterQuery &&
      options.propertyFilterQuery.tokens.length > 0
        ? propertyFilter(tableProps.allItems, options.propertyFilterQuery, {
            filteringProperties: tableProps.filteringProperties,
          })
        : tableProps.allItems,
    [
      tableProps.allItems,
      options.propertyFilterQuery,
      tableProps.filteringProperties,
    ]
  );

  return {
    data: filteredData,
    // A new object rather than an assignment into `tableProps` — that object
    // belongs to `useBuildTableProps` and writing through it corrupts the
    // caller's state.
    tableProps: { ...tableProps, allItems: filteredItems },
    // A settled-but-failed query must not look like a loaded-empty one
    // (RSP-6539): the `(!data && !error)` term keeps a widget in the loading
    // state until the query has actually produced one or the other, so a
    // failure renders the no-data state rather than a count of 0. It also
    // covers a hook whose `mapTrpcDataToGraphQL` returns undefined outright.
    // It is only correct while no `shouldSkip` is passed — a skipped query
    // reports loading: false with no data and no error. The same is true of a
    // `graphqlPermission`-gated hook, but only if the `useWidgetQuery`
    // contract in `types.ts` is violated: with `graphqlPermission` unset
    // `createQueryHook` binds the gate to a constant, so no widget hook has a
    // pending-gate window today.
    loading:
      loading || (!data && !error) || Boolean(tableProps.isEnrichmentLoading),
    // `createQueryHook` returns an error OBJECT (`QueryHookError`), not the
    // `isError` boolean the old tRPC leg read.
    error: Boolean(error),
  };
};
